import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { Component, ReactNode, useMemo } from "react";
import { crearTema } from "./theme";
import { useTema } from "./stores/temaStore";
import PublicLayout from "./components/public/PublicLayout";
import PublicHome from "./pages/public/Home";
import PublicTemas from "./pages/public/Temas";
import PublicTemaDetalle from "./pages/public/TemaDetalle";
import PublicPreguntas from "./pages/public/Preguntas";
import AppLayout from "./components/app/AppLayout";
import InstanciaPasos from "./pages/app/InstanciaPasos";
import ProcesoEstudianteVista from "./pages/app/ProcesoEstudianteVista";
import Estudiantes from "./pages/app/Estudiantes";
import EstudianteDetalle from "./pages/app/EstudianteDetalle";
import AppTemas from "./pages/app/Temas";
import AppTemaDetalle from "./pages/app/TemaDetalle";
import AppInicio from "./pages/app/Inicio";
import AppPreguntas from "./pages/app/Preguntas";
import Sugerencias from "./pages/app/Sugerencias";
import Comisiones from "./pages/app/Comisiones";
import ComisionCohorte from "./pages/app/ComisionCohorte";
import Coordinacion from "./pages/app/Coordinacion";
import Perfil from "./pages/app/Perfil";
import Evaluadores from "./pages/app/Evaluadores";
import EvaluadorDetalle from "./pages/app/EvaluadorDetalle";
import Externos from "./pages/app/Externos";
import Pasos from "./pages/app/Pasos";
import CorreosEnviados from "./pages/app/CorreosEnviados";
import Docentes from "./pages/app/Docentes";
import DocenteFicha from "./pages/app/DocenteFicha";
import RedirigirSiHaySesion from "./components/auth/RedirigirSiHaySesion";
import Login from "./pages/Login";
import { NuevaPassword, RecuperarPassword } from "./pages/Password";
import Items from "./pages/Items";
import NotFound from "./pages/NotFound";
import RequireAuth from "./components/auth/RequireAuth";
import DevSwitcher from "./components/dev/DevSwitcher";

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: "red", padding: 20, fontFamily: "monospace" }}>
          <h2>App Error</h2>
          <pre>{this.state.error?.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const viteBaseUrl = (import.meta as any).env?.BASE_URL || "/";
  const basename = viteBaseUrl === "/" ? "/" : viteBaseUrl.replace(/\/$/, "");

  const modo = useTema((s) => s.modo);
  const theme = useMemo(() => crearTema(modo), [modo]);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter basename={basename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            {/* Sitio público (Design Kit DCC) */}
            <Route element={<PublicLayout />}>
              <Route
                path="/"
                element={
                  <RedirigirSiHaySesion>
                    <PublicHome />
                  </RedirigirSiHaySesion>
                }
              />
              <Route path="/temas" element={<PublicTemas />} />
              <Route path="/temas/:id" element={<PublicTemaDetalle />} />
              <Route path="/preguntas" element={<PublicPreguntas />} />
              {/* Entrar y recuperar la contraseña son parte del sitio público, no
                  pantallas aparte: llevan su misma barra y se ven como el resto.
                  Quien ya tiene sesión no tiene nada que hacer en el login: se va a la
                  app, igual que desde la portada. Las de contraseña no se redirigen —
                  cambiarla teniendo sesión abierta es legítimo. */}
              <Route
                path="/login"
                element={
                  <RedirigirSiHaySesion>
                    <Login />
                  </RedirigirSiHaySesion>
                }
              />
              <Route path="/password/recuperar" element={<RecuperarPassword />} />
              <Route path="/password/nueva" element={<NuevaPassword />} />
            </Route>
            {/* App autenticada (Design Kit, barra lateral) */}
            <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route index element={<InstanciaPasos />} />
              <Route path="procesos/:rut" element={<ProcesoEstudianteVista />} />
              <Route path="estudiantes" element={<Estudiantes />} />
              <Route path="estudiantes/:rut" element={<EstudianteDetalle />} />
              <Route path="temas" element={<AppTemas />} />
              <Route path="docentes" element={<Docentes />} />
              <Route path="docentes/:rut" element={<DocenteFicha />} />
              <Route path="temas/:id" element={<AppTemaDetalle />} />
              <Route path="calendario" element={<Coordinacion />} />
              <Route path="evaluadores" element={<Evaluadores />} />
              <Route path="evaluadores/:rut" element={<EvaluadorDetalle />} />
              <Route path="comisiones" element={<Comisiones />} />
              <Route path="comisiones/:paso/:ano/:semestre" element={<ComisionCohorte />} />
              <Route path="pasos" element={<Pasos />} />
              <Route path="externos" element={<Externos />} />
              <Route path="correos" element={<CorreosEnviados />} />
              {/* Contenido del sitio público, también dentro de la app: al entrar
                  se pierde el header público y con él el acceso a estas páginas. */}
              <Route path="inicio" element={<AppInicio />} />
              <Route path="preguntas" element={<AppPreguntas />} />
              <Route path="sugerencias" element={<Sugerencias />} />
              <Route path="perfil" element={<Perfil />} />
            </Route>
            <Route path="/items" element={<RequireAuth><Items /></RequireAuth>} />
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <DevSwitcher />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
